import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../components/PageMeta';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Award, BookOpen, Building, Search, ShoppingBag, Users, Zap, DollarSign, CheckCircle, ChevronRight, Shield, Star, TrendingUp, ArrowUp, FileCheck, Sparkles, RefreshCw, FileText, MessageSquare, Eye, Gavel, Heart, FileSignature, Scale, FolderOpen, Download, Lightbulb, Globe, CheckSquare, Monitor, Lock, Calculator, Briefcase, XCircle, Edit, PieChart, UserCheck, ArrowRightLeft } from 'lucide-react';
import { mockData } from '../mock';
import PricingSection from '../components/PricingSection';
import TestimonialSlider from '../components/TestimonialSlider';
import Popup from '../components/Popup';
import MobileCardSlider from '../components/MobileCardSlider';
import useCounterAnimation from '../hooks/useCounterAnimation';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const iconMap = {
  Award,
  BookOpen,
  Building,
  Search,
  ShoppingBag,
  Users,
  Zap,
  DollarSign,
  CheckCircle,
  ArrowUp,
  RefreshCw,
  FileText,
  MessageSquare,
  Eye,
  Gavel,
  TrendingUp,
  Heart,
  Shield,
  FileSignature,
  Scale,
  FolderOpen,
  Download,
  Lightbulb,
  Globe,
  CheckSquare,
  Monitor,
  Lock,
  Calculator,
  Briefcase,
  XCircle,
  Edit,
  PieChart,
  UserCheck,
  ArrowRightLeft
};

const Home = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  // Use counter animation hooks with intersection observer
  const [trademarksCount, trademarksRef] = useCounterAnimation(10000, 2000, 300);
  const [successCount, successRef] = useCounterAnimation(98, 2000, 300);
  const [ratingCount, ratingRef] = useCounterAnimation(5, 1500, 300);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Add smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';
    
    // Back to top button visibility
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen page-transition">
      <PageMeta
        title="Trademark Registration & Brand Protection"
        description="Professional trademark registration services across the USA. Protect your brand identity with expert legal support from Global Trademark Federation."
      />
      {/* Hero Section - Balanced & Professional */}
      <section
        className="relative bg-cover bg-center pt-28 pb-16 md:pt-32 md:pb-20 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(45, 48, 71, 0.85), rgba(45, 48, 71, 0.70)), url(${mockData.heroImages.home})`,
        }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDEzNGg4di04aC04djh6bS0xNiAwaDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4em0tMTYgMGg4di04aC04djh6bS0xNiAwaDh2LThoLTh2OHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-10" />
        
        {/* Subtle Floating Shapes */}
        <div className="absolute top-20 left-10 w-24 h-24 bg-secondary/10 rounded-full blur-xl animate-pulse" />
        <div className="absolute top-40 right-20 w-32 h-32 bg-white/5 rounded-full blur-2xl animate-bounce" style={{ animationDuration: '3s' }} />
        <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-accent/15 rounded-full blur-lg animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Animated Lines */}
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-white/10 to-transparent" />
        <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-white/5 to-transparent" />
        
        <div className="container mx-auto px-4 text-center text-white relative z-10">
          {/* Badge with enhanced animation */}
          <div className="inline-block mb-4 animate-bounce">
            <span className="bg-gradient-to-r from-secondary/30 to-white/15 backdrop-blur-md text-white px-5 py-2 rounded-full text-xs md:text-sm font-bold border-2 border-white/30 shadow-xl flex items-center gap-2">
              <Star className="w-3 h-3 md:w-4 md:h-4 fill-secondary text-secondary animate-pulse" />
              #1 Trademark Services in USA
              <Star className="w-3 h-3 md:w-4 md:h-4 fill-secondary text-secondary animate-pulse" />
            </span>
          </div>
          
          {/* Main Heading - Reduced Size */}
          <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight animate-fade-in">
            <span className="inline-block hover:scale-105 transition-transform duration-300">
              {mockData.tagline}
            </span>
          </h1>
          
          {/* Subheading - Reduced Size */}
          <p className="text-lg md:text-xl mb-8 max-w-3xl mx-auto opacity-95 leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
            {mockData.heroDescription}
          </p>
          
          {/* CTA Buttons - Reduced Size */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <Button
              onClick={() => setIsPopupOpen(true)}
              size="lg"
              className="bg-secondary text-white hover:bg-secondary/90 font-bold text-base md:text-lg px-8 py-6 shadow-2xl hover:shadow-secondary/50 hover:scale-105 transition-all duration-300 group relative overflow-hidden"
            >
              <span className="relative z-10 flex items-center">
                Get Started Today
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white/10 backdrop-blur-md border-2 border-white text-white hover:bg-white hover:text-primary font-bold text-base md:text-lg px-8 py-6 transition-all hover:scale-105 duration-300 shadow-xl hover:shadow-white/50"
            >
              View Services
            </Button>
          </div>
          
          {/* Compact Trust Badges with hover effects and animated counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            <div ref={trademarksRef} className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 hover:border-secondary hover:scale-105 transition-all duration-300 group cursor-pointer shadow-lg animate-float" style={{ animationDelay: '0s' }}>
              <div className="text-3xl md:text-4xl font-bold mb-1 group-hover:text-secondary transition-colors">
                {trademarksCount.toLocaleString()}+
              </div>
              <div className="text-xs md:text-sm opacity-90 font-semibold">Trademarks Filed</div>
            </div>
            <div ref={successRef} className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 hover:border-secondary hover:scale-105 transition-all duration-300 group cursor-pointer shadow-lg animate-float" style={{ animationDelay: '0.5s' }}>
              <div className="text-3xl md:text-4xl font-bold mb-1 group-hover:text-secondary transition-colors">
                {successCount}%
              </div>
              <div className="text-xs md:text-sm opacity-90 font-semibold">Success Rate</div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 hover:border-secondary hover:scale-105 transition-all duration-300 group cursor-pointer shadow-lg animate-float" style={{ animationDelay: '1s' }}>
              <div className="text-3xl md:text-4xl font-bold mb-1 group-hover:text-secondary transition-colors">24/7</div>
              <div className="text-xs md:text-sm opacity-90 font-semibold">Support</div>
            </div>
            <div ref={ratingRef} className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:bg-white/20 hover:border-secondary hover:scale-105 transition-all duration-300 group cursor-pointer shadow-lg animate-float" style={{ animationDelay: '1.5s' }}>
              <div className="text-3xl md:text-4xl font-bold mb-1 group-hover:text-secondary transition-colors flex items-center justify-center gap-1">
                {ratingCount}
                <Star className="w-5 h-5 fill-secondary text-secondary" />
              </div>
              <div className="text-xs md:text-sm opacity-90 font-semibold">Client Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Third Informative Section - Brand Protection Benefits */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] bg-[size:60px_60px]" />
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-50 animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl opacity-50 animate-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
                Why Trademark Matters
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Protect What You've Built</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Your brand is your most valuable asset. Secure it with federal trademark protection and gain exclusive rights nationwide
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Left Side - Large Feature Card */}
            <div className="bg-white rounded-3xl p-10 shadow-2xl relative overflow-hidden group hover:shadow-3xl transition-all duration-500 border-2 border-gray-200">
              {/* Dark Background Image */}
              <div 
                className="absolute inset-0 bg-cover bg-center opacity-10"
                style={{
                  backgroundImage: 'url("https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60")',
                }}
              />
              <div className="relative z-10">
                <div className="bg-secondary/10 w-20 h-20 rounded-3xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="w-10 h-10 text-secondary" />
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Federal Legal Protection</h3>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Gain nationwide exclusive rights to your brand name, logo, or slogan. Stop competitors from using similar marks that could confuse customers.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
                    <span className="text-gray-900">Presumption of ownership in all 50 states</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
                    <span className="text-gray-900">Legal right to use ® symbol</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
                    <span className="text-gray-900">Enhanced damages in infringement cases</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Side - Three Smaller Cards */}
            <div className="space-y-6">
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary group">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:bg-primary/20 transition-all">
                    <TrendingUp className="w-7 h-7 text-secondary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-secondary transition-colors">Increase Brand Value</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Registered trademarks are valuable business assets that can be licensed, franchised, or sold
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary group">
                <div className="flex items-start gap-4">
                  <div className="bg-secondary/10 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:bg-secondary/20 transition-all">
                    <Building className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-secondary transition-colors">Business Expansion</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Secure your brand before expanding to new markets, products, or services
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary group">
                <div className="flex items-start gap-4">
                  <div className="bg-secondary/10 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:bg-secondary/20 transition-all">
                    <Award className="w-7 h-7 text-secondary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-secondary transition-colors">Consumer Trust</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Build credibility with customers who recognize registered trademarks as marks of quality
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden px-4">
            <MobileCardSlider showDots={true} autoScroll={false}>
              <div className="bg-white rounded-3xl p-8 shadow-2xl relative overflow-hidden border-2 border-gray-200">
                {/* Dark Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center opacity-10"
                  style={{
                    backgroundImage: 'url("https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60")',
                  }}
                />
                <div className="relative z-10">
                  <div className="bg-secondary/10 w-16 h-16 rounded-3xl flex items-center justify-center mb-4">
                    <Shield className="w-8 h-8 text-secondary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-900">Federal Legal Protection</h3>
                  <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                    Gain nationwide exclusive rights to your brand name, logo, or slogan
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-gray-900">Ownership in all 50 states</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-gray-900">Legal right to use ® symbol</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-gray-900">Enhanced damages protection</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10">
                <div className="bg-primary/10 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <TrendingUp className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Increase Brand Value</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Registered trademarks are valuable business assets that can be licensed, franchised, or sold
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10">
                <div className="bg-secondary/10 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <Building className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Business Expansion</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Secure your brand before expanding to new markets, products, or services
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10">
                <div className="bg-secondary/10 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <Award className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Consumer Trust</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Build credibility with customers who recognize registered trademarks as marks of quality
                </p>
              </div>
            </MobileCardSlider>
          </div>
        </div>
      </section>

      {/* Informative Section - Below Hero */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] bg-[size:60px_60px]" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-50" />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
                Why Choose Global Trademark Federation
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Your Trusted Trademark Partner</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We simplify the trademark registration process with expert guidance, transparent pricing, and dedicated support every step of the way
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-2 border-primary/10 hover:border-secondary group cursor-pointer relative overflow-hidden">
              <div className="absolute inset-0 bg-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="bg-secondary/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <Shield className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors">Comprehensive Protection</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Full trademark search, application filing, and monitoring services to protect your brand nationwide
                </p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-2 border-primary/10 hover:border-secondary group cursor-pointer relative overflow-hidden">
              <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="bg-primary/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <Users className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors">Expert Legal Team</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Experienced trademark attorneys and legal professionals guiding you through every step
                </p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-2 border-primary/10 hover:border-secondary group cursor-pointer relative overflow-hidden">
              <div className="absolute inset-0 bg-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="bg-secondary/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <DollarSign className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors">Transparent Pricing</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Clear, upfront pricing with no hidden fees. Know exactly what you're paying for from the start
                </p>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-2 border-primary/10 hover:border-secondary group cursor-pointer relative overflow-hidden">
              <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="bg-primary/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  <Zap className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors">Fast Processing</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Quick turnaround times with 24/7 support to keep your trademark application moving forward
                </p>
              </div>
            </div>
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden px-4">
            <MobileCardSlider showDots={true} autoScroll={false}>
              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10 relative overflow-hidden">
                <div className="bg-secondary/10 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <Shield className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Comprehensive Protection</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Full trademark search, application filing, and monitoring services to protect your brand nationwide
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10 relative overflow-hidden">
                <div className="bg-primary/10 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <Users className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Expert Legal Team</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Experienced trademark attorneys and legal professionals guiding you through every step
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10 relative overflow-hidden">
                <div className="bg-secondary/10 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <DollarSign className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Transparent Pricing</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Clear, upfront pricing with no hidden fees. Know exactly what you're paying for from the start
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-primary/10 relative overflow-hidden">
                <div className="bg-primary/10 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                  <Zap className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Fast Processing</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Quick turnaround times with 24/7 support to keep your trademark application moving forward
                </p>
              </div>
            </MobileCardSlider>
          </div>
        </div>
      </section>

      {/* Second Informative Section - How It Works */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] bg-[size:50px_50px]" />
        <div className="absolute top-10 left-10 w-72 h-72 bg-accent/10 rounded-full blur-3xl opacity-40" />
        <div className="absolute bottom-10 right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl opacity-40" />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
                How It Works
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Three Simple Steps to Protection</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our streamlined process makes trademark registration easy and stress-free
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Step 1 */}
            <div className="relative group">
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary">
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-xl rotate-12 group-hover:rotate-0 transition-transform duration-500">
                  01
                </div>
                <div className="mb-6">
                  <div className="bg-secondary/10 w-20 h-20 rounded-3xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Search className="w-10 h-10 text-secondary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-secondary transition-colors">Search & Consultation</h3>
                <p className="text-gray-600 leading-relaxed">
                  We conduct a comprehensive trademark search and provide expert consultation to ensure your mark is eligible for registration
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative group">
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary">
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-xl rotate-12 group-hover:rotate-0 transition-transform duration-500">
                  02
                </div>
                <div className="mb-6">
                  <div className="bg-primary/10 w-20 h-20 rounded-3xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <FileCheck className="w-10 h-10 text-secondary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-secondary transition-colors">Application Filing</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our legal team prepares and files your trademark application with the USPTO, handling all the complex paperwork for you
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative group">
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary">
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-xl rotate-12 group-hover:rotate-0 transition-transform duration-500">
                  03
                </div>
                <div className="mb-6">
                  <div className="bg-secondary/10 w-20 h-20 rounded-3xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Shield className="w-10 h-10 text-secondary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-secondary transition-colors">Monitor & Protect</h3>
                <p className="text-gray-600 leading-relaxed">
                  We monitor your application status and provide ongoing support until your trademark is fully registered and protected
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Asymmetric Layout */}
      <section id="services-section" className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl opacity-50" />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-4 py-2 rounded-full text-sm font-semibold">
                Our Services
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Comprehensive Legal Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Protect and grow your business with our expert trademark and intellectual property services
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {mockData.services.filter(service => 
              ['register-trademark', 'register-copyright', 'clearance-search', 'business-formation', 'amazon-brand-registry', 'wipo-registration'].includes(service.slug)
            ).map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Link
                  key={service.id}
                  to={`/services/${service.slug}`}
                >
                  <Card 
                    className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border-2 hover:border-accent cursor-pointer relative overflow-hidden hover-lift"
                  >
                    {/* Hover overlay effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    <CardContent className="p-8 relative z-10">
                      <div className="bg-gradient-to-br from-primary/10 to-accent/20 w-16 h-16 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                        <Icon className="w-8 h-8 text-secondary group-hover:scale-110 transition-transform" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors duration-300">{service.title}</h3>
                      <p className="text-gray-600 mb-6 leading-relaxed group-hover:text-gray-700 transition-colors">{service.shortDesc}</p>
                      <div className="text-secondary font-semibold flex items-center hover:text-secondary/80 group-hover:gap-3 transition-all duration-300 group-hover:translate-x-2">
                        Learn More <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden px-4">
            <MobileCardSlider showDots={true} autoScroll={false}>
              {mockData.services.filter(service => 
                ['register-trademark', 'register-copyright', 'clearance-search', 'business-formation', 'amazon-brand-registry', 'wipo-registration'].includes(service.slug)
              ).map((service) => {
                const Icon = iconMap[service.icon];
                return (
                  <Link
                    key={service.id}
                    to={`/services/${service.slug}`}
                  >
                    <Card 
                      className="group hover:shadow-2xl transition-all duration-500 border-2 hover:border-accent cursor-pointer relative overflow-hidden"
                    >
                      {/* Hover overlay effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      <CardContent className="p-6 relative z-10">
                        <div className="bg-gradient-to-br from-primary/10 to-accent/20 w-14 h-14 rounded-2xl flex items-center justify-center mb-4">
                          <Icon className="w-7 h-7 text-secondary" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors duration-300">{service.title}</h3>
                        <p className="text-gray-600 mb-4 leading-relaxed text-sm">{service.shortDesc}</p>
                        <div className="text-secondary font-semibold flex items-center hover:text-secondary/80 text-sm">
                          Learn More <ChevronRight className="ml-1 w-4 h-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </MobileCardSlider>
          </div>
        </div>
        
        {/* Strategic CTA after services */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-3xl p-8 max-w-4xl mx-auto border border-accent/20">
            <h3 className="text-2xl md:text-3xl font-bold text-primary mb-4">Ready to Get Started?</h3>
            <p className="text-gray-600 mb-6 text-lg">Join thousands of satisfied clients who trust us with their trademark needs</p>
            <Button
              onClick={() => setIsPopupOpen(true)}
              className="bg-secondary text-white hover:bg-secondary/90 font-bold px-8 py-4 hover:scale-105 transition-all duration-300"
            >
              Start Your Application Today
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Creative Cards */}
      <section className="py-20 bg-white relative">
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] bg-[size:60px_60px]" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-4 py-2 rounded-full text-sm font-semibold">
                Why Choose Us
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Your Success is Our Mission</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the difference with our industry-leading trademark services
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {mockData.whyChooseUs.map((item) => {
              const Icon = iconMap[item.icon];
              return (
                <div key={item.id} className="text-center group">
                  <div className="bg-gradient-to-br from-white to-primary/10 w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-2xl transition-all group-hover:scale-110 group-hover:rotate-3 border border-accent/20">
                    <Icon className="w-12 h-12 text-secondary" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden px-4">
            <MobileCardSlider showDots={true} autoScroll={false}>
              {mockData.whyChooseUs.map((item) => {
                const Icon = iconMap[item.icon];
                return (
                  <Card key={item.id} className="text-center bg-white border-2 border-gray-100 hover:border-accent transition-all">
                    <CardContent className="p-6">
                      <div className="bg-gradient-to-br from-white to-primary/10 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg border border-accent/20">
                        <Icon className="w-10 h-10 text-secondary" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                      <p className="text-gray-600 leading-relaxed text-sm">{item.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </MobileCardSlider>
          </div>
          
          {/* Strategic CTA after Why Choose Us */}
          <div className="text-center mt-16">
            <div className="bg-white rounded-3xl p-8 max-w-3xl mx-auto shadow-xl border-2 border-accent/20">
              <TrendingUp className="w-12 h-12 text-secondary mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-primary mb-3">Experience the Difference</h3>
              <p className="text-gray-600 mb-6">See why 98% of our clients recommend our services to others</p>
              <Button
                onClick={() => setIsPopupOpen(true)}
                variant="outline"
                className="border-2 border-secondary text-secondary hover:bg-secondary hover:text-white font-bold px-6 py-3 hover:scale-105 transition-all duration-300"
              >
                Get Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner - Middle of Page */}
      <section className="py-20 relative overflow-hidden" style={{ backgroundImage: `linear-gradient(135deg, rgba(45, 48, 71, 0.95), rgba(45, 48, 71, 0.85))` }}>
        {/* Abstract Background Pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.3' fill-rule='evenodd'/%3E%3C/svg%3E")`,
            backgroundSize: '100px 100px'
          }}
        />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block mb-6 animate-bounce">
              <div className="bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border border-white/30">
                <span className="text-white font-bold text-sm flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Limited Time Offer
                </span>
              </div>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Ready to Protect Your Brand?
            </h2>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed max-w-3xl mx-auto">
              Join thousands of successful businesses who trust us with their trademark registration. Get started today and secure your brand's future.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={() => setIsPopupOpen(true)}
                size="lg"
                className="bg-secondary hover:bg-secondary/90 text-white text-lg px-8 py-6 rounded-full shadow-2xl hover:shadow-secondary/50 hover:scale-105 transition-all duration-300 font-bold group"
              >
                Get Started Now
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => setIsPopupOpen(true)}
                size="lg"
                variant="outline"
                className="bg-white/10 backdrop-blur-md border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-8 py-6 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 font-bold"
              >
                Schedule Consultation
              </Button>
            </div>
            
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-white">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-secondary" />
                <span className="font-semibold">No Hidden Fees</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-secondary" />
                <span className="font-semibold">Expert Support</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-secondary" />
                <span className="font-semibold">Fast Processing</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection />

      {/* Testimonials */}
      <TestimonialSlider />

      {/* FAQ Section - Enhanced */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] bg-[size:60px_60px]" />
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl opacity-50" />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
                FAQ
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions about trademark registration
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {mockData.faqs.map((faq) => (
                <AccordionItem 
                  key={faq.id} 
                  value={`item-${faq.id}`} 
                  className="bg-white rounded-2xl px-8 border-2 border-primary/10 hover:border-secondary hover:shadow-lg transition-all duration-300 group"
                >
                  <AccordionTrigger className="text-left font-bold text-lg text-gray-900 hover:text-secondary py-6 group-hover:translate-x-1 transition-transform">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 pb-6 leading-relaxed text-base">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section - Gradient */}
      <section className="py-24 relative overflow-hidden text-white" style={{ backgroundImage: `linear-gradient(135deg, rgba(45, 48, 71, 0.95), rgba(45, 48, 71, 0.85))` }}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0aDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4em0tMTYgMGg4di04aC04djh6bS0xNiAwaDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4eiIvPjwvZz48L2c+PC9zdmc+')] opacity-20" />
        <div className="container mx-auto px-4 text-center relative">
          <Shield className="w-20 h-20 mx-auto mb-6 opacity-90" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Protect Your Brand?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-95 leading-relaxed">
            Start your trademark registration today and secure your business identity with our expert team
          </p>
          <Button
            onClick={() => setIsPopupOpen(true)}
            size="lg"
            className="bg-secondary text-white hover:bg-secondary/90 font-bold text-lg px-10 py-7 shadow-2xl hover:shadow-secondary/50 hover:scale-105 transition-all"
          >
            Get Started Now
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <Popup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
      
      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-secondary text-white p-4 rounded-full shadow-2xl hover:bg-secondary/90 hover:scale-110 transition-all duration-300 z-50 group"
          aria-label="Back to top"
        >
          <ArrowUp className="w-6 h-6 group-hover:animate-bounce" />
        </button>
      )}
    </div>
  );
};

export default Home;